import React, { useState, useEffect } from 'react';
import { Box, IconButton, MobileStepper, useTheme, Typography } from '@mui/material';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { IBannerReponse } from '../../../model/base/response/banner-response';
import { useMenuLinkNavigation } from '../../../hooks/useMenuLinkNavigation';

interface HeroBannerProps {
  banners: IBannerReponse[];
}

export default function HeroBanner({ banners }: HeroBannerProps) {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [imageErrors, setImageErrors] = useState<{ [key: number]: boolean }>({});
  const maxSteps = banners?.length || 0;
  const openLink = useMenuLinkNavigation();

  useEffect(() => {
    if (maxSteps === 0) return;

    const timer = setInterval(() => {
      setActiveStep((prevActiveStep) => (prevActiveStep + 1) % maxSteps);
    }, 5000);

    return () => clearInterval(timer);
  }, [maxSteps]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => (prevActiveStep + 1) % maxSteps);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => (prevActiveStep - 1 + maxSteps) % maxSteps);
  };

  const handleImageError = (index: number) => {
    setImageErrors((prev) => ({ ...prev, [index]: true }));
  };

  if (!banners || banners.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: { xs: 300, sm: 400, md: 500 },
        overflow: 'hidden',
        borderRadius: 2,
        boxShadow: 3,
      }}
    >
      {/* Banner Images */}
      {banners.map((banner, index) => (
        <Box
          key={index}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            opacity: index === activeStep ? 1 : 0,
            transition: 'opacity 0.8s ease-in-out',
            // Só o banner visível recebe clique (os demais ficam empilhados, transparentes)
            pointerEvents: index === activeStep ? 'auto' : 'none',
            cursor: banner.url ? 'pointer' : 'default',
          }}
          onClick={banner.url ? () => openLink(banner.url, banner.openInNewTab) : undefined}
        >
          {imageErrors[index] ? (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                backgroundColor: 'grey.200',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h5" color="text.secondary">
                Sem imagem
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                component="img"
                src={banner.urlImage}
                alt={banner.title || 'Banner'}
                onError={() => handleImageError(index)}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                }}
              />
              {/* Overlay for better text readability */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                  color: 'white',
                  p: 3,
                }}
              >
                {banner.title && (
                  <Box
                    sx={{
                      fontSize: { xs: '1.25rem', md: '2rem' },
                      fontWeight: 700,
                      textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                    }}
                  >
                    {banner.title}
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      ))}

      {/* Navigation Arrows */}
      <IconButton
        onClick={handleBack}
        sx={{
          position: 'absolute',
          left: 16,
          top: '50%',
          transform: 'translateY(-50%)',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
          },
        }}
      >
        <KeyboardArrowLeft />
      </IconButton>

      <IconButton
        onClick={handleNext}
        sx={{
          position: 'absolute',
          right: 16,
          top: '50%',
          transform: 'translateY(-50%)',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
          },
        }}
      >
        <KeyboardArrowRight />
      </IconButton>

      {/* Dots Indicator */}
      <MobileStepper
        variant="dots"
        steps={maxSteps}
        position="static"
        activeStep={activeStep}
        sx={{
          position: 'absolute',
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'transparent',
          '& .MuiMobileStepper-dot': {
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
          },
          '& .MuiMobileStepper-dotActive': {
            backgroundColor: 'white',
          },
        }}
        nextButton={<></>}
        backButton={<></>}
      />
    </Box>
  );
}
